// Apolo IDE — Task DAG (sprint tasking-cursor-ide, P3-T01).
// @arch:pure-engine
//
// Pure, framework-free model for the Kiro-style Requirements→Design→Tasks
// graph that lives between the composer and the agent loop. The doctrine
// (01-DOCTRINE.md [T1]) requires this module to import NO React, NO
// Electron, NO `window.*`, and NO `fetch(`. Wire it at the edges (the
// React component, the agent-turn route, the IPC handler), never inside.
//
// Task shape (frozen contract, 03-PLAN §6):
//   { id: string,           // unique per run
//     name: string,
//     deps: string[],       // task ids
//     status: 'pending'|'in_progress'|'green'|'failed'|'blocked',
//     acceptance?: string } // optional acceptance criteria
//
// Public API:
//   buildGraph(tasks)            -> Graph
//   topoOrder(graph)             -> string[]   (throws on cycle / unknown dep)
//   readySet(graph, statusMap)   -> string[]   (ids ready to start now — deps all green)
//   markStatus(statusMap, id, s) -> Map        (returns a NEW Map; never mutates)
//   criticalPath(graph)          -> string[]   (longest path through the DAG)
//   waves(graph, statusMap)      -> string[][] (parallel waves grouped by readiness)
//
// Errors thrown carry a `code` field so callers can branch without
// regex-matching messages.

export const TASK_STATUS = Object.freeze({
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  GREEN: 'green',
  FAILED: 'failed',
  BLOCKED: 'blocked',
});

const STATUS_SET = new Set(Object.values(TASK_STATUS));

export class TaskGraphError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.name = 'TaskGraphError';
    this.code = code;
    Object.assign(this, extra);
  }
}

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function normalizeTask(raw) {
  const id = String(raw.id ?? '').trim();
  if (!id) {
    throw new TaskGraphError('INVALID_TASK', 'task is missing id', { task: raw });
  }
  const name = String(raw.name ?? '').trim();
  if (!name) {
    throw new TaskGraphError('INVALID_TASK', `task ${id} is missing name`, { task: raw });
  }
  const deps = asArray(raw.deps)
    .filter((d) => d != null)
    .map((d) => String(d).trim())
    .filter(Boolean);
  const status = STATUS_SET.has(raw.status) ? raw.status : TASK_STATUS.PENDING;
  const out = { id, name, deps, status };
  if (raw.acceptance != null) out.acceptance = String(raw.acceptance);
  return out;
}

/**
 * Build an immutable graph from a list of tasks. Validates:
 *   - tasks is an array
 *   - every task has id + name
 *   - ids are unique
 *   - every dep refers to a known id
 *
 * Returns: { tasks, byId, allIds }
 *   - tasks: normalized Task[]
 *   - byId: Map<id, Task>
 *   - allIds: string[] in insertion order
 *
 * Cycles are NOT checked here — callers run topoOrder() to surface them
 * lazily (cheaper for snapshots that only need the graph for rendering).
 */
export function buildGraph(rawTasks) {
  if (!Array.isArray(rawTasks)) {
    throw new TaskGraphError('INVALID_INPUT', 'tasks must be an array');
  }
  const tasks = rawTasks.map(normalizeTask);
  const byId = new Map();
  for (const t of tasks) {
    if (byId.has(t.id)) {
      throw new TaskGraphError('DUPLICATE_ID', `duplicate task id: ${t.id}`, { id: t.id });
    }
    byId.set(t.id, t);
  }
  for (const t of tasks) {
    for (const dep of t.deps) {
      if (!byId.has(dep)) {
        throw new TaskGraphError(
          'UNKNOWN_DEP',
          `task ${t.id} depends on unknown task ${dep}`,
          { id: t.id, dep },
        );
      }
    }
  }
  const allIds = tasks.map((t) => t.id);
  return Object.freeze({ tasks, byId, allIds });
}

/**
 * Kahn's topological order. Throws TaskGraphError('CYCLE') if the graph is
 * cyclic. The error carries `.cycle: string[]` listing the ids participating
 * in the cycle (best effort — the remaining nodes after Kahn drains).
 */
export function topoOrder(graph) {
  const { byId, allIds } = graph;
  const inDegree = new Map();
  const reverse = new Map(); // dep -> tasks that depend on dep
  for (const id of allIds) {
    inDegree.set(id, 0);
    reverse.set(id, []);
  }
  for (const id of allIds) {
    for (const dep of byId.get(id).deps) {
      inDegree.set(id, inDegree.get(id) + 1);
      reverse.get(dep).push(id);
    }
  }
  const queue = [];
  for (const [id, d] of inDegree) if (d === 0) queue.push(id);
  // Stable order = insertion order; sort only the seeds by allIds index.
  const indexOf = new Map(allIds.map((id, i) => [id, i]));
  queue.sort((a, b) => indexOf.get(a) - indexOf.get(b));
  const order = [];
  while (queue.length) {
    const head = queue.shift();
    order.push(head);
    const downstream = reverse.get(head).slice().sort(
      (a, b) => indexOf.get(a) - indexOf.get(b),
    );
    for (const next of downstream) {
      const d = inDegree.get(next) - 1;
      inDegree.set(next, d);
      if (d === 0) queue.push(next);
    }
  }
  if (order.length !== allIds.length) {
    const remaining = allIds.filter((id) => !order.includes(id));
    throw new TaskGraphError(
      'CYCLE',
      `task graph has a cycle (remaining: ${remaining.join(', ')})`,
      { cycle: remaining },
    );
  }
  return order;
}

/**
 * The current "ready set" — task ids whose deps are all `green` and whose
 * own status is `pending`. These form one "wave" that can run concurrently
 * (FEATURES.md §1: Kiro Waves = readySet of the next topological layer).
 *
 * statusMap: Map<id, status> | Record<id, status>. Missing entries fall
 * back to the task's own .status (so a freshly-built graph works without
 * a separate map).
 */
export function readySet(graph, statusMap) {
  const status = (id) => {
    if (statusMap instanceof Map) {
      return statusMap.get(id) ?? graph.byId.get(id).status;
    }
    if (statusMap && typeof statusMap === 'object') {
      return statusMap[id] ?? graph.byId.get(id).status;
    }
    return graph.byId.get(id).status;
  };
  const out = [];
  for (const id of graph.allIds) {
    const own = status(id);
    if (own !== TASK_STATUS.PENDING) continue;
    const depsGreen = graph.byId.get(id).deps.every(
      (d) => status(d) === TASK_STATUS.GREEN,
    );
    if (depsGreen) out.push(id);
  }
  return out;
}

/**
 * Return a NEW status map with `id` set to `status`. Never mutates. Accepts
 * Map<id, status> or plain object and returns the same shape.
 */
export function markStatus(statusMap, id, status) {
  if (!STATUS_SET.has(status)) {
    throw new TaskGraphError('INVALID_STATUS', `unknown status: ${status}`, { id, status });
  }
  if (statusMap instanceof Map) {
    const next = new Map(statusMap);
    next.set(id, status);
    return next;
  }
  return { ...(statusMap || {}), [id]: status };
}

/**
 * Longest path through the DAG (a.k.a. critical path). Returns task ids
 * in execution order. Empty array for an empty graph. Used by the Task
 * Board to highlight the spine and by Plan-Mode to estimate "shortest
 * time to done" given parallelism.
 */
export function criticalPath(graph) {
  if (graph.allIds.length === 0) return [];
  const order = topoOrder(graph);
  // distance[id] = longest path ending at id (1-based length)
  const distance = new Map(order.map((id) => [id, 1]));
  const parent = new Map();
  for (const id of order) {
    const cur = distance.get(id);
    for (const next of graph.allIds) {
      if (graph.byId.get(next).deps.includes(id)) {
        const cand = cur + 1;
        if (cand > (distance.get(next) || 0)) {
          distance.set(next, cand);
          parent.set(next, id);
        }
      }
    }
  }
  let tail = order[0];
  let max = 0;
  for (const [id, d] of distance) {
    if (d > max) {
      max = d;
      tail = id;
    }
  }
  const path = [];
  let cur = tail;
  while (cur != null) {
    path.unshift(cur);
    cur = parent.get(cur);
  }
  return path;
}

/**
 * Partition the graph into parallel waves: each wave is a readySet given
 * the previous wave is fully green. Useful for "Run all tasks" UX
 * (FEATURES.md §1 — animated parallel progress). Pure / deterministic.
 *
 * The current statusMap is the starting point — already-green tasks are
 * skipped; in-progress/failed/blocked tasks halt their downstream and are
 * NOT included.
 */
export function waves(graph, statusMap) {
  const out = [];
  let state = statusMap instanceof Map
    ? new Map(statusMap)
    : { ...(statusMap || {}) };
  // Seed the state with task's own status if not present.
  for (const id of graph.allIds) {
    const has = state instanceof Map ? state.has(id) : id in state;
    if (!has) {
      if (state instanceof Map) state.set(id, graph.byId.get(id).status);
      else state[id] = graph.byId.get(id).status;
    }
  }
  // Cap iterations at allIds.length to defend against pathological cycles
  // (we'd have already thrown via topoOrder if a real cycle existed; this
  // is a belt-and-suspenders for callers that bypass it).
  for (let i = 0; i < graph.allIds.length + 1; i += 1) {
    const wave = readySet(graph, state);
    if (wave.length === 0) break;
    out.push(wave);
    for (const id of wave) {
      state = markStatus(state, id, TASK_STATUS.GREEN);
    }
  }
  return out;
}

/**
 * Convenience: compute done/total over a statusMap. Returns `{ done,
 * total, ratio }`. Counts only tasks present in the graph.
 */
export function progress(graph, statusMap) {
  const total = graph.allIds.length;
  if (total === 0) return { done: 0, total: 0, ratio: 0 };
  let done = 0;
  for (const id of graph.allIds) {
    const s = statusMap instanceof Map
      ? statusMap.get(id)
      : statusMap && statusMap[id];
    const effective = s ?? graph.byId.get(id).status;
    if (effective === TASK_STATUS.GREEN) done += 1;
  }
  return { done, total, ratio: done / total };
}

/**
 * Map a Plan-Mode proposal's todos → a linear DAG of tasks for the right-rail
 * TasksPanel (`code.tasks`). Each todo becomes a node; node i depends on i-1 so
 * the board renders the Cursor-style linear pipeline (rail + line). IDs are
 * forced sequential (`t1..tN`) so buildGraph never throws on duplicate/unknown
 * ids regardless of what the model emitted. Pure — easy to unit-test.
 *
 * @param {Array<{id?:string,text?:string,label?:string,action?:string}>} todos
 * @returns {{id:string,name:string,deps:string[],status:string}[]}
 */
export function planTodosToTasks(todos) {
  if (!Array.isArray(todos)) return [];
  const clean = todos
    .map((t) => String((t && (t.text ?? t.label ?? t.action)) || '').trim())
    .map((name, i) => ({ name, i }))
    .filter((x) => x.name);
  return clean.map(({ name }, i) => ({
    id: `t${i + 1}`,
    name,
    deps: i > 0 ? [`t${i}`] : [],
    status: TASK_STATUS.PENDING,
  }));
}
